import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFirestore, AngularFirestoreCollection, AngularFirestoreDocument } from 'angularfire2/firestore';
import { FirestoreService } from './firestore/firestore.service';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/operator/mergeMap';

import { Globals } from '../globals';
import { FsAuto } from './auto.service';
import { EmployeeId } from './employee.service';

export interface Remission {
  active: number;
  createdAt: any;
  updatedAt: any;

  customerRef: any;
  fiscalDataRef: any;
  addressRef: any;
  
  productionOrder: ProductionOrder;

  folio: string;
  description: string;
  quantity: number;
  date: string;

  boxs: Box[];

  billRefs: string[];
  billQuantitys: number[];
  status: number;

  autoId: FsAuto['id'];
  distanceTraveledKm: number;
  dateArrival: string;
  timeArrival: string;
  weightBoxesKg: number;
  driverEmployeeId: EmployeeId['id'];
  license: string;
  permitSCT: string;

  qualityCertificate: QualityCertificate;
  qualityHistory: QualityCertificate[];
}

export interface RemissionId extends Remission {
  id: string;
}

export interface ProductionOrder {
  id: string;
  folio: string;
  quantity: number;
  prClientCode: string;
  prClientVersion: string;
  prClientBarCode: string;
  customerPurchaseOrderNumber: string;
}
export interface Box {
  amount: number;
  pieces: number;
}
export interface QualityCertificate{
  status: string;
  evalGuide: string;
  date: Date;
  sampleAmount: number;
  odpSection: string;
  lotIsComplete: boolean;
  notes: string;
  defects: Defect[];
  aqls: any;

  userId: string;
  userName?: string;
}
export interface Defect{
  description: string;
  quantity: number;
  aql: string;
}

@Injectable()
export class RemissionService {

  remissionCollection: AngularFirestoreCollection<Remission>;
  remissions: any;

  remissionDocument: AngularFirestoreDocument<RemissionId>;
  remission: Observable<RemissionId>;

  collection: string;
  customerCollection: string;
  fiscalDataCollection: string;
  addressCollection: string;
  billCollection: string;

  // NOTE: Change this any for Remission Interface
  remission$: Observable<any>;
  remissions$: Observable<any[]>;

  constructor(
    private afs: AngularFirestore,
    public afh: FirestoreService,
    private router: Router
  ) {
    this.collection = Globals.REMISSION_COLLECTION;
    this.customerCollection = Globals.CUSTOMER_SUPPLIER_COLLECTION;
    this.fiscalDataCollection = Globals.FISCAL_DATA_COLLECTION;
    this.addressCollection = Globals.ADDRESSES_COLLECTION;
    this.billCollection = Globals.CUSTOMER_BILL_COLLECTION;
  }

  /**
   * Get all remissions with references
   * @return {Observable} Arrays of remissions
   */
  getRemissions({customerId = '', fromDate = '', fetchRefs = true} = {}) {
    let col;
    if(customerId){
      col = this.afh.colWithIds$(this.collection, ref => ref.where('active', '==', true).where('customerRef', '==', this.customerReference(customerId)) );
    }else if(fromDate){
      col = this.afh.colWithIds$(this.collection, ref => ref.orderBy('date','desc').where('active', '==', true).where('date', '>=', fromDate) );
    }else{
      col = this.afh.colWithIds$(this.collection, ref => ref.orderBy('folio','desc').where('active', '==', true) );
    }
    if(!fetchRefs) return col;
    return col.map(documents => {
      return documents.map(document => {
        const customerRef = this.afh.docWithIds$(`${document.customerRef.path}`);
        const fiscalDataRef = this.afh.docWithIds$(`${document.fiscalDataRef.path}`); 
        const addressRef = this.afh.docWithIds$(`${document.addressRef.path}`);
        //const billRefs = (document.billRefs || []).map(billId => this.afh.docWithIds$(`${this.billCollection}/${billId}`));
  
        //console.log(`Got rem: ${document.id}`);
        return Observable.combineLatest(
          customerRef,
          fiscalDataRef,
          addressRef,
          (customerData, fiscalDataData, addressData) => ({ customer: customerData, fiscalData: fiscalDataData, address: addressData})
        ).map( data => ({ customerObj: data.customer, fiscalDataObj: data.fiscalData, addressObj: data.address, ...document }) );
      });
    }).mergeMap(addresssDocuments => Observable.combineLatest(addresssDocuments));
  }

  /**
  * Get remission data
  * @param {string} sid - The id of the remission
  * @return {Observable} The Remission Object
  */
  getRemission(sid: string) {
    return this.remission$ = this.afh.docWithIds$(this.collection + '/' + sid).map(document => {
      const customerRef = this.afh.docWithIds$(`${document.customerRef.path}`);
      const fiscalDataRef = this.afh.docWithIds$(`${document.fiscalDataRef.path}`);
      const addressRef = this.afh.docWithIds$(`${document.addressRef.path}`);
      //const billRefs = (document.billRefs || []).map(billId => this.afh.docWithIds$(`${this.billCollection}/${billId}`));

      return Observable.combineLatest(
        customerRef,
        fiscalDataRef,
        addressRef,
        (customerData, fiscalDataData, addressData) => ({ customer: customerData, fiscalData: fiscalDataData, address: addressData})
      ).map( data => ({ customerObj: data.customer, fiscalDataObj: data.fiscalData, addressObj: data.address, ...document }) );
    }).mergeMap(addresssDocuments => Observable.combineLatest(addresssDocuments));
  }

  /**
  * Create new remission
  * @param {Remission} remission - The data of the remission
  */
  addRemission(remission: Remission) {
    remission.customerRef = this.afh.doc(`${this.customerCollection}/${remission.customerRef}`).ref;
    remission.fiscalDataRef = this.afh.doc(`${this.fiscalDataCollection}/${remission.fiscalDataRef}`).ref;
    remission.addressRef = this.afh.doc(`${this.addressCollection}/${remission.addressRef}`).ref;
    return this.afh.add(`${this.collection}`, remission);
  }

  /**
   * Update current remission
   * @param {string} sid  - The id of the remission
   * @param {Remission} remission - The data of the remission
   */
  updateRemission(sid: string, remission: Remission) {
    remission.customerRef = this.afh.doc(`${this.customerCollection}/${remission.customerRef}`).ref;
    remission.fiscalDataRef = this.afh.doc(`${this.fiscalDataCollection}/${remission.fiscalDataRef}`).ref;
    remission.addressRef = this.afh.doc(`${this.addressCollection}/${remission.addressRef}`).ref;
    return this.afh.update(`${this.collection}/${sid}`, remission);
  }

  updateByField(object: Partial<RemissionId>, id: string){
    return this.afh.update(`${this.collection}/${id}`, object);
  }

  get afhService(){
    return this.afh;
  }

  /**
   * Delete current remission
   * @param {string} sid - The id of the remission
   */
  deleteRemission(sid: string) {
    this.afh.delete(`${this.collection}/${sid}`);
  }

  customerReference(customerId){
    return this.afh.doc(`${this.customerCollection}/${customerId}`).ref;
  }

  windowReplace(action: string, id?: string){
    switch(action){
      case 'add':
      case 'create':
        this.router.navigate([`/new_remission/${id}`]);
        break;
      case 'edit':
      case 'update':
        this.router.navigate([`/edit_remission/${id}`]);
        break;
      case 'certificate':
        this.router.navigate([`/remissionCertificate/${id}`]);
        break;
      case 'view':
      case 'display':
        this.router.navigate([`/vw_remission/${id}`]);
        break;
      case 'read':
        this.router.navigate([`/remission`]);
        break;
      case 'viewOdp':
        this.router.navigate([`/vw_production_order/${id}`]);
        break;
      default:
        console.log('Not an action');
    }
  }
}

export class RemissionData {
  certificateStatus = {
    'key1': { name: 'Pendiente' },
    'key2': { name: 'Aprobado' },
    'key3': { name: 'Rechazado' },
  }

  inspectionTypes = {
    'key1': { name: 'Normal' },
    'key2': { name: 'Severa' },
  }
}